import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError } from '~/api/client'

/*
 * The state behind a press that has to wait — AGENTS.md, and `/specs/app.spec.md`, A press
 * that waits. What a working control looks like is `components/working.tsx`; this is when it
 * is working, and when it says so.
 */

/** How long work runs before its control says it is working — `/specs/design.spec.md`. */
export const WORKING_BEAT_MS = 150

/**
 * Whether a control that is working says so yet: false for the first beat of the work, true
 * after it for as long as the work goes on.
 */
export function useBeat(working: boolean): boolean {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (!working) {
      return
    }
    const timer = window.setTimeout(() => {
      setShown(true)
    }, WORKING_BEAT_MS)
    return () => {
      window.clearTimeout(timer)
      // Put back for the next work, which gets its own beat.
      setShown(false)
    }
  }, [working])
  return working && shown
}

/**
 * Whether what a press handed back is work to wait on — a promise — rather than nothing. A
 * control that takes either works on the one and simply acts on the other.
 */
export function isWork(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { then?: unknown }).then === 'function'
  )
}

/**
 * Work a press started that did not go through, carrying the words that say why. Thrown by the
 * work itself, so whatever it was pressed from can say it where the press was, and stay to be
 * tried again. A confirmation is what reads it — see `confirm-dialog.tsx`; a menu item's work
 * says its own failures, since a menu is no place to stand a line of them.
 */
export class Refused extends Error {
  readonly said: string

  constructor(said: string) {
    super(said)
    this.name = 'Refused'
    this.said = said
  }
}

/** What a press says where the server it waited on could not be reached. */
export const NOT_REACHED = 'That did not reach us. Try again in a moment.'

/**
 * What a press says where the server was reached and turned the work down: trying the same
 * thing again would only be turned down again, so it asks for a fresh look instead.
 */
export const TURNED_DOWN = 'That could not be done as things stand. Close this and look again.'

/** What a press says of work that failed without saying why itself. */
export const DID_NOT_GO_THROUGH = 'That did not go through. Try again.'

/** The words a failure is said in: its own where it carries them, and a plain failure's else. */
export function saidOf(error: unknown): string {
  return error instanceof Refused ? error.said : DID_NOT_GO_THROUGH
}

/**
 * A call to the server, refused in the words [NOT_REACHED] where it did not go through — no
 * connection, or the server failing in itself — and in [TURNED_DOWN] where the server answered
 * and would not do it. [unreached] says the first differently, for a call that has words of its
 * own for needing a connection. Only the call goes in here: what the device does with the answer
 * comes after it, so a failure there is never said as the server not having been reached — and
 * never invites trying again what the server already did.
 */
export async function reached<T>(call: () => Promise<T>, unreached = NOT_REACHED): Promise<T> {
  try {
    return await call()
  } catch (error) {
    throw new Refused(error instanceof ApiError && error.status < 500 ? TURNED_DOWN : unreached)
  }
}

/**
 * A removal the server answers with "there is no such thing": what was asked for is already
 * so — removed from another device, say — and it is done rather than refused.
 */
export async function removed(call: () => Promise<unknown>): Promise<void> {
  try {
    await call()
  } catch (error) {
    if (!(error instanceof ApiError && error.status === 404)) {
      throw error
    }
  }
}

export interface Work {
  /** True from the moment the work starts until it is done, however it ends. */
  readonly working: boolean
  /** True once the work has run past the beat — the spinner is standing. */
  readonly shown: boolean
  /**
   * Runs [work] unless work of this control is already running, in which case the press is
   * ignored and nothing is run. A failure leaves the control as it was and is handed on to the
   * caller, to be said however the screen already says it.
   */
  readonly run: <T>(work: () => Promise<T>) => Promise<T | undefined>
}

/**
 * The work one control starts. The guard is a ref as well as a state: two presses inside one
 * frame both see the state from before either of them, and only the ref knows the first has
 * already begun.
 */
export function useWork(): Work {
  const running = useRef(false)
  const [working, setWorking] = useState(false)
  const shown = useBeat(working)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const run = useCallback(async <T>(work: () => Promise<T>): Promise<T | undefined> => {
    if (running.current) {
      return undefined
    }
    running.current = true
    setWorking(true)
    try {
      return await work()
    } finally {
      running.current = false
      if (mounted.current) {
        setWorking(false)
      }
    }
  }, [])

  return { working, shown, run }
}
